import { Component } from '@theme/component';
import { scrollIntoView } from '@theme/scrolling';

/**
 * Blog tabs component - 简单的 tab 切换，无 AJAX 加载
 *
 * @extends {Component}
 */
export default class BlogTabsComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    const tabs = this.querySelectorAll('.blog-tabs__tab');
    this.tabsContainer = this.querySelector('.blog-tabs__tabs');

    // 绑定 tab 点击事件
    tabs.forEach((tab) => {
      if (tab instanceof HTMLElement) {
        tab.addEventListener('click', this.#handleTabClick.bind(this));
      }
    });
  }

  /**
   * 处理 tab 点击
   * @param {Event} event
   */
  #handleTabClick(event) {
    const tab = event.currentTarget;
    if (!(tab instanceof HTMLElement)) return;

    // 如果点击的是当前激活的 tab，不处理
    if (tab.classList.contains('is-active')) return;

    const blogHandle = tab.dataset.blogHandle;
    if (!blogHandle) {
      console.warn('Blog handle is missing');
      return;
    }

    // 更新 tab 状态
    this.#updateTabs(tab);

    // 更新 panel 状态
    const targetPanel = this.querySelector(
      `[data-blog-handle="${blogHandle}"][role="tabpanel"]`
    );
    if (!(targetPanel instanceof HTMLElement)) return;

    this.#updatePanels(targetPanel);
  }

  /**
   * 更新 tab 状态
   * @param {HTMLElement} activeTab
   */
  #updateTabs(activeTab) {
    const tabs = this.querySelectorAll('.blog-tabs__tab');
    tabs.forEach((t) => {
      t.classList.remove('is-active');
      t.setAttribute('aria-selected', 'false');
    });

    activeTab.classList.add('is-active');
    activeTab.setAttribute('aria-selected', 'true');

    // 滚动激活的 tab 到居中位置（所有设备）
    if (this.tabsContainer instanceof HTMLElement) {
      scrollIntoView(activeTab, {
        ancestor: this.tabsContainer,
        behavior: 'smooth',
        block: 'start',
        inline: 'center',
      });
    }
  }

  /**
   * 更新 panel 状态
   * @param {HTMLElement} activePanel
   */
  #updatePanels(activePanel) {
    const panels = this.querySelectorAll('.blog-tabs__panel');
    panels.forEach((p) => {
      if (p instanceof HTMLElement) {
        p.classList.remove('is-active');
        p.hidden = true;
        // 重置已隐藏 panel 的卡片状态
        const cards = p.querySelectorAll('.blog-tabs__article-item');
        cards.forEach((card) => {
          if (card instanceof HTMLElement) {
            card.classList.remove('blog-tabs__card--animate');
            card.style.removeProperty('--animation-delay');
            card.style.removeProperty('opacity');
            card.style.removeProperty('transform');
          }
        });
      }
    });

    activePanel.classList.add('is-active');
    activePanel.hidden = false;

    // 标记 panel 已动画化
    activePanel.setAttribute('data-animated', 'true');

    // 触发卡片动画（延迟一下确保 panel 已显示）
    setTimeout(() => {
      this.#animateCards(activePanel);
    }, 10);
  }

  /**
   * 为卡片添加依次出现的动画
   * @param {HTMLElement} panel
   */
  #animateCards(panel) {
    const cards = panel.querySelectorAll('.blog-tabs__article-item');
    
    if (cards.length === 0) return;

    // 移除之前的动画类和样式
    cards.forEach((card) => {
      if (card instanceof HTMLElement) {
        card.classList.remove('blog-tabs__card--animate');
        card.style.removeProperty('--animation-delay');
      }
    });

    // 使用双重 requestAnimationFrame 确保 DOM 完全更新后再添加动画
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        cards.forEach((card, index) => {
          if (card instanceof HTMLElement) {
            // 设置动画延迟，每个卡片延迟 50ms
            const delay = index * 50;
            card.style.setProperty('--animation-delay', `${delay}ms`);
            card.classList.add('blog-tabs__card--animate');
          }
        });
      });
    });
  }
}

if (!customElements.get('blog-tabs-component')) {
  customElements.define('blog-tabs-component', BlogTabsComponent);
}
